import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { firstValueFrom, of, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { UserAuthService } from 'src/app/auth/services/user-auth.service';
import { OAuthService } from 'src/app/components/content-oauth/services/oauth.service';
import { EncodingService } from 'src/app/storage/services/encoding.service';
import { FileItem } from 'src/app/storage/services/file-uploader.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { UploadManagerConfig } from 'src/app/storage/services/upload-manager.service';
import { environment } from 'src/environments/environment';
import { BasicUploaderComponent } from '../../components/basic-uploader/basic-uploader.component';
import { AttributeDataService } from '../../services/attribute-data.service';
import {
  CanvaConnectApiService,
  CanvaDesign,
  CanvaExportFormat,
} from '../../services/canva-connect-api.service';
import { ComponentsService } from '../../services/components.service';
import {
  ImportType,
  MultiStageProgressService,
} from '../../services/multi-stage-progress.service';
import { ProgressStage } from '../../services/multi-stage-progress.types';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import {
  CANVA_PROVIDER,
  CANVA_SCOPE,
  CanvaService,
} from '../services/canva.service';

export interface CanvaDesignData {
  id?: string;
  name: string;
  type: 'image' | 'video' | 'document';
  source: string;
  templateDefault?: boolean;
}

@Component({
  selector: 'component-canva-connect',
  templateUrl: './canva-connect.component.html',
  styleUrls: ['./canva-connect.component.scss'],
  standalone: false,
})
export class CanvaConnectComponent implements OnInit, OnDestroy {
  designs: CanvaDesign[] = [];
  selectedPlaceholder: number | null = null;
  selectedExportFormats: CanvaExportFormat | null = null;
  continuationToken: string | null = null;
  private searchSubject$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  public componentId: string;
  public userAccount: string;
  public componentAccount: string;
  public spinner: boolean;
  public userAuthorized: boolean;
  public authenticateFailed: boolean;
  public componentDisconnected: boolean;

  connectedAccountDisplayName: string = '';
  selectedDesign: CanvaDesignData | null = null;
  refreshInterval: number = 720;
  volume: number = 0;
  isImporting: boolean = false;
  loadingDesigns: boolean = false;
  loadingExportFormats: boolean = false;
  accessToken: string = '';
  uploadManager: UploadManagerConfig;
  fileUploadList: File[] = [];
  isUploading: boolean = false;
  validFileType: 'image' | 'video' | 'document' =
    StorageManagerService.FILE_TYPE.IMAGE;
  private uploadCompletionCount: number = 0;
  overallProgress: number = 0;
  uploadError: string | null = null;
  private hasUploadFailure: boolean = false;
  protected exportFormat: keyof CanvaExportFormat | null = null;
  private hadExistingDesign: boolean = false;
  private existingDesignBackup: CanvaDesignData | null = null;
  private isCancelling: boolean = false;
  private progressSubscription: Subscription;

  @ViewChild(BasicUploaderComponent) basicUploader: BasicUploaderComponent;

  filterConfig = {
    placeholder: 'Search your Canva designs...',
    id: 'canva-design-search',
  };

  search = {
    query: '',
  };

  exportAsVideo: boolean = false;

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private authService: OAuthService,
    private canvaService: CanvaService,
    private userAuthService: UserAuthService,
    private canvaConnectApi: CanvaConnectApiService,
    private storageUtilsService: StorageUtilsService,
    private storageManagerService: StorageManagerService,
    private templateEditorUtilsService: TemplateEditorUtilsService,
    private encodingService: EncodingService,
    private progressMapper: MultiStageProgressService,
  ) {
    this.spinner = false;
    this.userAuthorized = false;
    this.authenticateFailed = false;
    this.componentDisconnected = false;
    this.userAccount = this.authService.getUserIdentifier(CANVA_PROVIDER);
    this.componentAccount = null;
    componentsService.registerDirective({
      type: 'rise-canva-connect',
      element: this.elementRef.nativeElement,
      show: () => {
        this.componentId = this.componentsService.selected.id;
        this._init();
        this._validateAndLoad();
      },
      getName: (componentId: string): string => {
        const name = this.attributeDataService.getAvailableAttributeData(
          componentId,
          'designName',
        );
        return name || 'Canva Design';
      },
      isAuthenticating: (): boolean => {
        return !this.userAuthorized;
      },
    });

    this.uploadManager = {
      alwaysOverwrite: true,
      onUploadStatus: (isUploading: boolean) => {
        this.isUploading = isUploading;
        if (!isUploading && this.isCancelling) {
          this.isCancelling = false;
        }
      },
      onCompleteItem: (item: FileItem) => {
        if (!this.isImporting || this.isCancelling) {
          return;
        }
        if (item && this.fileUploadList.length > 0) {
          this.uploadCompletionCount++;
          if (!item.isSuccess) {
            this.uploadError = "Your file couldn't be uploaded.";
            this.hasUploadFailure = true;
          }
          if (this.uploadCompletionCount >= this.fileUploadList.length) {
            if (this.hasUploadFailure) {
              if (this.hadExistingDesign && this.existingDesignBackup) {
                this.selectedDesign = this.existingDesignBackup;
              } else {
                this.selectedDesign = null;
                this.selectedPlaceholder = null;
              }
            }
            const filePath = this.storageUtilsService.getFilePath(item.file);
            this.finalizeImport(filePath);
          }
        }
      },
      onProgressItem: (item: any, progress: number) => {
        if (this.isImporting && !this.isCancelling) {
          this.progressMapper.updateFileProgress(progress);
          if (!this.hasUploadFailure) {
            this.uploadError = null;
          }
          this.changeDetectorRef.detectChanges();
        }
      },
      isSingleFileSelector: () => true,
    };
    Object.defineProperty(this.uploadManager, 'folderPath', {
      get: () =>
        `.assets/.asset${this.userAccount}_${this.selectedDesign?.id}/canva/`,
    });

    this.searchSubject$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((query) => {
          this.loadingDesigns = true;

          if (this.userAuthorized && this.accessToken) {
            return this.canvaConnectApi.getDesigns(
              this.accessToken,
              null,
              30,
              query,
            );
          }
          return of({ items: [], continuation: null });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response: any) => {
          // Only reset selection when we get new search results
          this.designs = response?.items || [];
          this.continuationToken = response?.continuation || null;
          this.selectedPlaceholder = null;
          this.loadingDesigns = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          console.error('[Canva] Error loading designs:', error);
          this.loadingDesigns = false;
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  ngOnInit(): void {
    this.progressSubscription = this.progressMapper.progress$.subscribe(
      (progress) => {
        this.overallProgress = progress.overall;
      },
    );
  }

  ngOnDestroy(): void {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject$.complete();
  }

  doSearch = () => {
    this.searchSubject$.next(this.search.query);
  };

  get sameAccount(): boolean {
    return this.userAccount === this.componentAccount;
  }

  _init(): void {
    this.selectedPlaceholder = null;
    this.fileUploadList = [];
    this.designs = [];
    this.continuationToken = null;
    this.search.query = '';
    this.exportAsVideo = false;
    this._loadComponentData();
  }

  _loadComponentData(): void {
    const source = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'source',
    );
    const account = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'account',
    );
    const interval = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'refreshInterval',
    );
    const designName = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'designName',
    );
    let fileType = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'fileType',
    );
    const volume =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'volume',
      ) || 0;
    this.volume = this.templateEditorUtilsService.intValueFor(volume, 0);

    this.refreshInterval = interval || 720;

    if (source) {
      if (!fileType) {
        let pathname = '';
        try {
          const url = new URL(source);
          pathname = url.pathname;
        } catch (error) {
          pathname = source;
        }
        // Strip query string and hash to avoid obscuring file extension
        pathname = pathname.split(/[?#]/)[0];
        fileType = this.storageManagerService.getFileType(pathname);
      }
      this.validFileType = fileType || StorageManagerService.FILE_TYPE.IMAGE;
      this.selectedDesign = {
        name: designName || 'Canva Design',
        type: this.validFileType,
        source,
        templateDefault: !designName,
      };
    } else {
      this.selectedDesign = null;
      this.validFileType = StorageManagerService.FILE_TYPE.IMAGE;
    }
  }

  _validateAndLoad() {
    this.componentAccount = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'account',
    );
    this.userAccount = this.authService.getUserIdentifier(CANVA_PROVIDER);

    this._validateCredentials();
  }

  private async _validateCredentials() {
    this.spinner = true;
    this.userAuthorized = false;

    try {
      let status: any;
      try {
        status = await this.authService.getConnectionStatus(
          CANVA_PROVIDER,
          CANVA_SCOPE,
          null,
        );
      } catch (error) {
        // fallback to _getStatus
        const response = (await this.authService._getStatus(
          CANVA_PROVIDER,
          null,
          CANVA_SCOPE,
        )) as HttpResponse<any>;
        const body = response.body || {};
        if (body.notAuthorizedScopes && body.notAuthorizedScopes.length > 0) {
          status = {
            authenticated: false,
            notAuthorizedScopes: body.notAuthorizedScopes,
            username: body.username,
          };
        } else {
          throw error;
        }
      }

      if (status.notAuthorizedScopes && status.notAuthorizedScopes.length > 0) {
        this.userAuthorized = false;
        this.authenticateFailed = true;
        throw new Error(
          `Missing required scopes: ${status.notAuthorizedScopes.join(', ')}`,
        );
      }

      if (!status.authenticated) {
        this.userAuthorized = false;
        throw new Error('User not authenticated');
      }

      this.userAuthorized = true;

      const promises: Promise<any>[] = [];

      if (this.componentAccount) {
        promises.push(
          this.authService
            .getUsername(CANVA_PROVIDER, this.componentAccount)
            .then(({ authenticated }) => {
              this.componentDisconnected = !authenticated;
            }),
        );
      }

      promises.push(
        this.authService
          .getUsername(CANVA_PROVIDER, this.userAccount)
          .then(({ authenticated, accessToken }) => {
            if (authenticated && accessToken) {
              this.accessToken = accessToken;
              return this._loadUserProfile(accessToken);
            }
            return Promise.resolve(null);
          }),
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Failed to get Canva credentials:', error);
      // still continue final .finally block handling
    }

    if (!this.componentAccount) {
      await this._saveAccount(this.userAccount);
    }

    try {
      if (this.userAuthorized && this.accessToken) {
        this.load();
      }
    } catch (error) {
      console.error(
        '[Canva] Account not connected or missing scopes. Error:',
        error,
      );
      this.userAuthorized = false;
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private _loadUserProfile(accessToken: string): Promise<void> {
    return new Promise((resolve) => {
      this.canvaConnectApi
        .getUserProfile(accessToken)
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            this.connectedAccountDisplayName =
              response?.profile?.display_name || 'Canva User';
            this.changeDetectorRef.detectChanges();
            resolve();
          },
          error: (error) => {
            console.error('[Canva] Failed to load user profile:', error);
            this.connectedAccountDisplayName = '';
            this.changeDetectorRef.detectChanges();
            resolve();
          },
        });
    });
  }

  async _connectAccount() {
    this.spinner = true;
    try {
      await this.authService.authenticate(CANVA_PROVIDER, CANVA_SCOPE);

      this.authenticateFailed = false;
      this.componentDisconnected = false;

      this.userAccount = this.authService.getUserIdentifier(CANVA_PROVIDER);

      const status = await this.authService.getConnectionStatus(
        CANVA_PROVIDER,
        CANVA_SCOPE,
        null,
      );

      if (status.notAuthorizedScopes && status.notAuthorizedScopes.length > 0) {
        console.error(
          '[Canva] Authentication succeeded but missing scopes:',
          status.notAuthorizedScopes,
        );
        this.authenticateFailed = true;
        this.userAuthorized = false;
        throw new Error(
          `Authentication succeeded but required scopes were not granted: ${status.notAuthorizedScopes.join(', ')}`,
        );
      }

      if (!status.authenticated) {
        console.error(
          '[Canva] Authentication succeeded but user not authenticated',
        );
        this.authenticateFailed = true;
        this.userAuthorized = false;
        throw new Error('Authentication succeeded but user not authenticated');
      }

      this.userAuthorized = true;

      const { accessToken } = await this.authService.getUsername(
        CANVA_PROVIDER,
        this.userAccount,
      );

      if (accessToken) {
        this.accessToken = accessToken;
        await this._loadUserProfile(accessToken);
      }

      await this._saveAccount(this.userAccount);

      if (this.accessToken) {
        this.load();
      }
    } catch (err) {
      console.error('[Canva] Failed to connect or verify scopes:', err);
      this.authenticateFailed = true;
      this.userAuthorized = false;
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  authenticate() {
    this.authenticateFailed = false;
    this.userAuthService.removeEventListenerVisibilityAPI();

    this._connectAccount().finally(() => {
      this.userAuthService.addEventListenerVisibilityAPI();
      this.changeDetectorRef.detectChanges();
    });
  }

  private _saveAccount(account: string) {
    this.componentAccount = account;
    this._saveAttributeData();
  }

  saveVolume() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'volume',
      this.volume,
    );
  }

  private _saveAttributeData() {
    if (this.sameAccount || !this.componentAccount) {
      this.attributeDataService.setAttributeData(
        this.componentId,
        'account',
        this.componentAccount,
      );
    }
  }

  load(): void {
    if (this.loadingDesigns || this.hasDesign() || !this.accessToken) {
      return;
    }

    // Only load if list is empty OR we have a continuation token
    if (this.designs.length > 0 && !this.continuationToken) {
      return;
    }

    this.loadingDesigns = true;

    this.canvaConnectApi
      .getDesigns(
        this.accessToken,
        this.continuationToken,
        30,
        this.search.query,
      )
      .subscribe({
        next: (response) => {
          this.designs = this.designs.concat(response.items);
          this.continuationToken = response.continuation || null;

          this.loadingDesigns = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          console.error('[Canva] Error loading designs:', error);
          this.loadingDesigns = false;
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  async _revokeAccount() {
    try {
      const revoked: boolean = await this.canvaService.revoke(CANVA_PROVIDER);
      this.userAuthorized = false;
      if (!revoked) {
        console.log('Token could not be revoked');
      }
    } catch (err: any) {
      console.log('Failed to revoke account', err.message);
    }
  }

  async disconnect() {
    this.spinner = true;

    try {
      await this._revokeAccount();
      this.connectedAccountDisplayName = '';
      this.userAuthorized = false;
      this.accessToken = '';
      this.componentDisconnected = false;
      this.authenticateFailed = false;
      this.designs = [];
      this.continuationToken = null;
      this.search.query = '';
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  hasDesign(): boolean {
    return !!this.selectedDesign;
  }

  canEditInCanva(): boolean {
    return this.accessToken && this.userAuthorized;
  }

  async onPlaceholderClick(index: number): Promise<void> {
    this.selectedPlaceholder = index;
    this.selectedExportFormats = null;
    this.exportFormat = null;

    this.loadingExportFormats = true;

    try {
      const formats = await firstValueFrom(
        this.canvaConnectApi.getExportFormats(
          this.designs[index].id,
          this.accessToken,
        ),
      );

      if (this.selectedPlaceholder === index) {
        this.selectedExportFormats = formats;
        this.exportFormat = this.canvaConnectApi.determineExportFormat(
          this.selectedExportFormats,
          this.designs[index].page_count,
        );
        if (this.exportFormat === 'mp4') {
          this.exportAsVideo = true;
        } else if (this.exportFormat === 'pdf') {
          this.exportAsVideo = false;
        }
      }
    } catch (error) {
      console.error('[Canva] Error getting export formats:', error);
    } finally {
      this.loadingExportFormats = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  getImportButtonText(): string {
    switch (this.exportFormat) {
      case 'mp4':
        return 'Import This Video';
      case 'pdf':
        return 'Import This Document';
      case 'png':
        return 'Import This Image';
      default:
        return 'Import This File';
    }
  }

  saveExportAsVideo(exportAsVideo: boolean): void {
    this.exportAsVideo = exportAsVideo;
    if (exportAsVideo) {
      this.exportFormat = 'mp4';
    } else {
      this.exportFormat = this.canvaConnectApi.determineExportFormat(
        this.selectedExportFormats,
        this.designs[this.selectedPlaceholder].page_count,
        this.exportAsVideo,
      );
    }
  }

  isSelected(index: number): boolean {
    return this.selectedPlaceholder === index;
  }

  onImportFileClick(): Promise<void> {
    if (
      this.selectedPlaceholder !== null &&
      this.designs[this.selectedPlaceholder]
    ) {
      const selectedDesign = this.designs[this.selectedPlaceholder];

      this.hadExistingDesign = !!this.selectedDesign?.source;
      if (this.hadExistingDesign) {
        this.existingDesignBackup = { ...this.selectedDesign };
      }

      return this.importFile(selectedDesign);
    }
  }

  async importFile(selectedDesign: CanvaDesign): Promise<void> {
    try {
      const userAccount = this.authService.getUserIdentifier(CANVA_PROVIDER);
      // intentionally do not await so it doesn't block
      const connectionStatusPromise = this.authService.getConnectionStatus(
        CANVA_PROVIDER,
        CANVA_SCOPE,
      );

      const fileType = this.storageManagerService.getFileType(
        '.' + this.exportFormat,
      ) as 'image' | 'video' | 'document';
      this.validFileType = fileType || StorageManagerService.FILE_TYPE.IMAGE;
      this.isImporting = true;
      this.isCancelling = false;
      this.fileUploadList = [];
      this.uploadCompletionCount = 0;
      this.uploadError = null;
      this.hasUploadFailure = false;
      this.progressMapper.start(this.validFileType as ImportType);
      this.selectedDesign = {
        id: selectedDesign.id,
        name: '',
        type: this.validFileType,
        source: '',
      };
      this.changeDetectorRef.detectChanges();

      const result = await this.canvaConnectApi.importDesign(
        selectedDesign,
        this.accessToken,
        this.exportFormat,
        (stage: ProgressStage, progress: number) => {
          if (!this.isCancelling) {
            this.progressMapper.updateStageProgress(stage, progress);
            this.changeDetectorRef.detectChanges();
          }
        },
      );

      if (this.isCancelling) return;

      this.selectedDesign.name = result.designTitle;

      const files = result.files || [];
      this.attributeDataService.setMultipleAttributeData(this.componentId, {
        type: 'canva',
        designId: result.designId,
        designName: result.designTitle,
        fileType: this.validFileType,
        environment: environment.production ? 'prod' : 'test',
        cacheBuster:
          files[0]?.lastModified?.toString() || Date.now().toString(),
      });

      if (this.exportFormat === 'pdf') {
        this.finalizeImport(result.exportUrl);
      } else {
        const files = result.files.map((file, index) => {
          const filename =
            result.files.length > 1
              ? `${userAccount}_${result.designId}_${index + 1}.${this.exportFormat}`
              : `${userAccount}_${result.designId}.${this.exportFormat}`;
          return new File([file], filename, { type: file.type });
        });

        this.fileUploadList = files;
      }

      await connectionStatusPromise;

      if (this.isCancelling) return;
      if (this.sameAccount || !this.componentAccount) {
        this.attributeDataService.setAttributeData(
          this.componentId,
          'account',
          userAccount,
        );
      }

      this.attributeDataService.setAttributeData(
        this.componentId,
        'forceRefresh',
        Date.now().toString(),
      );
    } catch (error) {
      if (this.isCancelling) return;
      console.error('Error importing design:', error);
      this.isImporting = false;
      this.isCancelling = false;
      if (!this.hadExistingDesign) {
        this.selectedDesign = null;
        this.selectedPlaceholder = null;
      } else {
        this.selectedDesign = this.existingDesignBackup;
      }
      this.fileUploadList = [];
      this.uploadCompletionCount = 0;
      this.uploadError = null;
      this.hasUploadFailure = false;
      this.progressMapper.reset();
      this.hadExistingDesign = false;
      this.existingDesignBackup = null;
      this.changeDetectorRef.detectChanges();
    }
  }

  private finalizeImport(source: string): void {
    if (this.selectedDesign) {
      this.selectedDesign.source = source;
    }
    this.attributeDataService.setMultipleAttributeData(this.componentId, {
      source,
      forceRefresh: Date.now().toString(),
    });
    this.isImporting = false;
    this.fileUploadList = [];
    this.uploadCompletionCount = 0;
    this.progressMapper.reset();
    this.hasUploadFailure = false;
    this.hadExistingDesign = false;
    this.existingDesignBackup = null;
    this.changeDetectorRef.detectChanges();
  }

  selectDesign(): void {
    this.selectedDesign = null;
    if (this.userAuthorized && this.accessToken) {
      this.load();
    } else {
      this.changeDetectorRef.detectChanges();
    }
  }

  saveRefreshInterval(): void {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'refreshInterval',
      this.refreshInterval,
    );
  }

  async refreshDesign(): Promise<void> {
    const designId = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'designId',
    );
    if (designId) {
      try {
        const design = await this.canvaConnectApi.getDesignById(
          designId,
          this.accessToken,
        );

        this.exportFormat = this.fileTypeToFormat(this.selectedDesign.type);

        await this.importFile(design);
      } catch (error) {
        console.error('Error refreshing design:', error);
      }
    }
  }

  private fileTypeToFormat(fileType: string): keyof CanvaExportFormat {
    switch (fileType) {
      case 'image':
        return 'png';
      case 'video':
        return 'mp4';
      case 'document':
        return 'pdf';
      default:
        throw new Error(`Invalid file type: ${fileType}`);
    }
  }

  async editInCanva(): Promise<void> {
    const designId = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'designId',
    );
    if (designId && this.accessToken && this.userAuthorized) {
      try {
        const design = await this.canvaConnectApi.getDesignById(
          designId,
          this.accessToken,
        );
        if (design?.urls?.edit_url) {
          window.open(design.urls.edit_url, '_blank');
        }
      } catch (error) {
        console.error('Error getting design:', error);
      }
    }
  }

  cancelImport(): void {
    this.isCancelling = true;

    if (this.basicUploader?.uploader) {
      this.basicUploader.uploader.cancelAllUploads();
    }

    if (this.progressMapper.isEncodingActive()) {
      this.encodingService.disableEncoding();
    }

    this.isImporting = false;
    this.fileUploadList = [];
    this.uploadCompletionCount = 0;
    this.uploadError = null;
    this.hasUploadFailure = false;
    this.progressMapper.reset();

    if (this.hadExistingDesign) {
      this.selectedDesign = this.existingDesignBackup;
      this.selectedPlaceholder = null;
      this.hadExistingDesign = false;
      this.existingDesignBackup = null;
    } else {
      this.selectedDesign = null;
      this.selectedPlaceholder = null;
    }

    this.changeDetectorRef.detectChanges();
  }

  disconnectFromCanva(): void {
    this.disconnect();
    this.selectedDesign = null;
  }
}
